import { useState } from 'react'
import { Button, Input, TinyMCE, FileDropzone } from 'components'
import Flex from 'components/priority/Flex/Flex'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useMayMakeVerseGlobal } from '@/hooks/utility/useMayMakeVerseGlobal'
import { ToggleSwitch } from 'components/global/ToggleSwitch/ToggleSwitch'
import { PersonalImageCard } from 'components/dashboard/CaseDetails'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useTranslation } from 'react-i18next'
import { IMAGE_MIME_WILDCARD } from '@messenger/consts'
import {
  removeStylizedVerseImage,
  uploadStylizedVerseImage,
} from '@/api/customVerses'

export const CustomVerseEditor = ({
  value,
  reference: [reference, setReference],
  image: [verseImage, setVerseImage],
  TinyMCERef,
  cancel,
  save,
  type,
  verseId,
  global: [isGlobalVerse, setIsGlobalVerse],
  themeVerse: [isThemeVerse, setIsThemeVerse],
}) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('customVerseEditor')
  const isAdmin = useIsAdmin()
  // Hidden, not disabled, for a caller the server refuses a global verse.
  const mayMakeVerseGlobal = useMayMakeVerseGlobal()
  const [imagesLoading, setImagesLoading] = useState(false)

  const stylizedVerse = type === 'IMAGE'

  const canUploadVerseImage = stylizedVerse && !verseImage

  const uploadVerseImage = async (file) => {
    setImagesLoading(true)
    try {
      const fd = new FormData()
      fd.append('file', file[0], 'blob')
      const resp = await uploadStylizedVerseImage(fd)
      setImagesLoading(false)
      if (resp.data) setVerseImage(resp.data)
      return resp.data
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'uploadStylizedVerseImage',
      )
      throw error
    }
  }

  const removeVerseImage = async () => {
    try {
      const resp = await removeStylizedVerseImage(verseId, {
        filename: verseImage,
      })
      if (resp) setVerseImage(null)
    } catch (error) {
      handleError(
        error?.response?.data?.message || error.message,
        error,
        null,
        'removeVerseImage',
      )
    }
  }

  return (
    <Flex column full>
      {!stylizedVerse && (
        <Flex
          border="1px solid"
          borderColor="gray4"
          radius="2px"
          minHeight="200px"
          margin="0 0 12px 0"
          full
          background="lockedWhite"
        >
          <TinyMCE
            placeholder={t(
              'editor.menus.assets.stylizedVerses.typeTextPlaceholder',
            )}
            value={value}
            setRef={(ref) => (TinyMCERef.current = ref)}
          />
        </Flex>
      )}

      {canUploadVerseImage && (
        <Flex full column>
          <FileDropzone
            height="200px"
            margin="0 0 24px 0"
            text={t(
              'editor.menus.assets.stylizedVerses.dragAndDropPlaceholder',
            )}
            fileTypes={[IMAGE_MIME_WILDCARD]}
            allowMultiple={false}
            onDrop={(file) => uploadVerseImage(file)}
            isLoading={imagesLoading}
          />
        </Flex>
      )}

      {stylizedVerse && verseImage && (
        <Flex full column width="100%">
          <PersonalImageCard
            image={verseImage}
            index={0}
            makeDefault={true}
            margin="0 0 24px 0"
            deleteImage={() => removeVerseImage()}
            allowDelete={true}
          />
        </Flex>
      )}

      <Input
        margin="0 0 12px 0"
        label={t('editor.menus.assets.stylizedVerses.referenceLabel')}
        value={[reference, setReference]}
      />
      {isAdmin && (
        <Flex
          justify="flex-start"
          align="flex-start"
          margin="16px 0"
          gap="8px 0"
          column
        >
          {mayMakeVerseGlobal && (
            <Tooltip
              text={t('editor.menus.assets.stylizedVerses.makeVerseGlobalText')}
              margin="0 12px 0 0"
            >
              <ToggleSwitch
                label={t(
                  'editor.menus.assets.stylizedVerses.isVerseGlobalText',
                )}
                labelRight
                value={[isGlobalVerse, setIsGlobalVerse]}
                disabled={!isAdmin}
              />
            </Tooltip>
          )}
          {stylizedVerse && !isGlobalVerse && (
            <Tooltip
              text={t(
                'editor.menus.assets.stylizedVerses.linkVerseToProductText',
              )}
              margin="0 12px 0 0"
            >
              <ToggleSwitch
                label={t(
                  'editor.menus.assets.stylizedVerses.isLinkedVerseText',
                )}
                labelRight
                value={[isThemeVerse, setIsThemeVerse]}
                disabled={!isAdmin}
              />
            </Tooltip>
          )}
        </Flex>
      )}
      <Flex>
        <Button onClick={() => cancel?.()} margin="0 12px 0 0">
          {t('editor.menus.assets.stylizedVerses.cancelButton')}
        </Button>
        <Button primary onClick={() => save?.()}>
          {t('editor.menus.assets.stylizedVerses.saveButton')}
        </Button>
      </Flex>
    </Flex>
  )
}

export default CustomVerseEditor
